import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Loader2, AlertCircle, Clock, Eye, Database, User as UserIcon, LogIn, MessageSquare, ChevronDown, ChevronRight, ChevronUp, ShieldCheck } from 'lucide-react';
import axios from 'axios';
import { API_ENDPOINTS } from '../config';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

// =============================================================================
// Taurus-specific rendering utilities (collapsible PDFs + markdown-lite)
// =============================================================================

// Escape HTML to prevent XSS
const escapeHtml = (str) => {
  const div = document.createElement('div');
  div.textContent = str;
  return div.innerHTML;
};

// Markdown-lite renderer for Taurus output (headers, bold, tables only)
const renderTaurusMarkdown = (text) => {
  if (!text || typeof text !== 'string') return '';

  // Parse markdown tables
  const parseTable = (tableText) => {
    const lines = tableText.trim().split('\n');
    if (lines.length < 2) return tableText;

    const rows = lines.map(line =>
      line.split('|').map(cell => cell.trim()).filter(cell => cell.length > 0)
    );

    const header = rows[0];
    const body = rows.slice(2);

    let html = '<table class="border-collapse border border-gray-300 dark:border-gray-600 my-3 min-w-full">';
    html += '<thead class="bg-gray-100 dark:bg-gray-700"><tr>';
    header.forEach(cell => {
      html += `<th class="border border-gray-300 dark:border-gray-600 px-3 py-2 text-left text-gray-900 dark:text-white font-semibold">${cell}</th>`;
    });
    html += '</tr></thead><tbody>';
    body.forEach(row => {
      html += '<tr class="hover:bg-gray-50 dark:hover:bg-gray-800">';
      row.forEach(cell => {
        html += `<td class="border border-gray-300 dark:border-gray-600 px-3 py-2 text-gray-800 dark:text-gray-100">${cell}</td>`;
      });
      html += '</tr>';
    });
    html += '</tbody></table>';
    return html;
  };

  // Escape HTML first
  let processed = escapeHtml(text);

  // Store tables temporarily
  const tables = [];
  processed = processed.replace(
    /(?:^|\n)((?:\|.+\|\n)+)/gm,
    (match, tableText) => {
      if (tableText.includes('---')) {
        const placeholder = `__TABLE_${tables.length}__`;
        tables.push(parseTable(tableText));
        return '\n' + placeholder + '\n';
      }
      return match;
    }
  );

  // Process markdown
  processed = processed
    .replace(/^### (.+)$/gm, (match, content) =>
      `<h3 class="text-lg font-semibold mt-3 mb-1 text-gray-900 dark:text-white">${content}</h3>`)
    .replace(/^## (.+)$/gm, (match, content) =>
      `<h2 class="text-xl font-bold mt-3 mb-1 text-gray-900 dark:text-white">${content}</h2>`)
    .replace(/\*\*(.+?)\*\*/g, (match, content) =>
      `<strong class="font-semibold text-gray-900 dark:text-white">${content}</strong>`)
    .replace(/\n/g, '<br/>');

  // Restore tables
  tables.forEach((tableHtml, i) => {
    processed = processed.replace(`__TABLE_${i}__`, tableHtml);
  });

  return processed;
};

// Collapsible section for PDF outputs
const CollapsiblePdfOutput = ({ title, content, defaultCollapsed = true }) => {
  const [isCollapsed, setIsCollapsed] = useState(defaultCollapsed);

  return (
    <div className="my-1">
      <button
        onClick={() => setIsCollapsed(!isCollapsed)}
        className="flex items-center gap-1 text-xs text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 font-medium"
      >
        {isCollapsed ? <ChevronRight className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
        {title}
      </button>
      {!isCollapsed && (
        <div className="mt-1 pl-4 border-l-2 border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-400">
          <pre className="whitespace-pre-wrap text-xs">{content}</pre>
        </div>
      )}
    </div>
  );
};

// Parse Taurus content into parts (text vs collapsible outputs)
// Uses combined regex to match FILES_LIST, PDF, and TOOL_CODE blocks in a single pass
const parseTaurusContent = (content) => {
  if (!content) return [];

  // Combined regex to match FILES_LIST, PDF, and TOOL_CODE blocks in a single pass
  // Group 1-3: FILES_LIST (full match, count, content)
  // Group 4-9: PDF (full match, filename, start page, end page, total pages, content)
  // Group 10-12: TOOL_CODE (full match, title with icon, content)
  const combinedRegex = /(?:(FILES_LIST: \((\d+) files?\)\n={50}\n([\s\S]*?)--- END FILES_LIST ---\n)|(PDF: (.+?) \(pages (\d+)-(\d+) of (\d+)\)\n={50}\n([\s\S]*?)--- END PDF ---\n)|(TOOL_CODE: (.+?)\n={50}\n([\s\S]*?)--- END TOOL_CODE ---\n))/g;

  const parts = [];
  let lastIndex = 0;
  let match;
  let toolCodeCounter = 0;

  while ((match = combinedRegex.exec(content)) !== null) {
    // Add text content before this match
    if (match.index > lastIndex) {
      const beforeContent = content.slice(lastIndex, match.index);
      if (beforeContent.trim()) {
        parts.push({ type: 'text', content: beforeContent, key: `text-${lastIndex}` });
      }
    }

    // Determine which pattern matched and build the part
    if (match[1]) {
      // FILES_LIST matched
      const title = `📁 Available Files (${match[2]})`;
      parts.push({
        type: 'files',
        title: title,
        content: match[3].trim(),
        key: `files-${title}`
      });
    } else if (match[4]) {
      // PDF matched
      const title = `📄 ${match[5]} (pages ${match[6]}-${match[7]} of ${match[8]})`;
      parts.push({
        type: 'pdf',
        title: title,
        content: match[9].trim(),
        key: `pdf-${title}`
      });
    } else if (match[10]) {
      // TOOL_CODE matched (Python or SQL code block)
      toolCodeCounter++;
      const title = match[11].trim(); // e.g., "🐍 Python (1234 chars) — import..."
      parts.push({
        type: 'code',
        title: title,
        content: match[12].trim(),
        key: `code-${toolCodeCounter}-${match.index}`
      });
    }

    lastIndex = match.index + match[0].length;
  }

  // Add remaining content after last match
  if (lastIndex < content.length) {
    const remaining = content.slice(lastIndex);
    if (remaining.trim()) {
      parts.push({ type: 'text', content: remaining, key: `text-${lastIndex}` });
    }
  }

  // If no matches at all, return content as single text part
  if (parts.length === 0 && content.trim()) {
    parts.push({ type: 'text', content: content, key: 'text-0' });
  }

  return parts;
};

// Taurus content renderer with collapsible PDFs and markdown
const TaurusContentRenderer = ({ content }) => {
  const parts = useMemo(() => parseTaurusContent(content), [content]);

  if (parts.length === 0) return null;

  // No PDF outputs - render with markdown
  if (parts.length === 1 && parts[0].type === 'text') {
    return (
      <div
        className="whitespace-pre-wrap text-xs font-mono"
        dangerouslySetInnerHTML={{ __html: renderTaurusMarkdown(parts[0].content) }}
      />
    );
  }

  // Mixed content
  return (
    <>
      {parts.map(part => {
        if (part.type === 'pdf' || part.type === 'files') {
          return (
            <CollapsiblePdfOutput
              key={part.key}
              title={part.title}
              content={part.content}
              defaultCollapsed={true}
            />
          );
        }
        // Code blocks (Python/SQL) - collapsible with syntax-appropriate styling
        if (part.type === 'code') {
          return (
            <details key={part.key} className="my-2 border border-gray-300 dark:border-gray-600 rounded-lg overflow-hidden">
              <summary className="cursor-pointer px-3 py-2 bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 text-sm font-medium text-gray-700 dark:text-gray-300 flex items-center gap-2">
                <span>{part.title}</span>
              </summary>
              <pre className="p-3 bg-gray-900 dark:bg-gray-950 text-gray-100 text-xs overflow-x-auto max-h-96 overflow-y-auto whitespace-pre-wrap">
                {part.content}
              </pre>
            </details>
          );
        }
        return (
          <div
            key={part.key}
            className="whitespace-pre-wrap text-xs font-mono"
            dangerouslySetInnerHTML={{ __html: renderTaurusMarkdown(part.content) }}
          />
        );
      })}
    </>
  );
};

// =============================================================================
// End Taurus-specific utilities
// =============================================================================

/**
 * SharedChatView - Public view for shared chats
 *
 * Features:
 * - Displays shared chat without requiring authentication (for public shares)
 * - Shows organization-only shares only to org members
 * - Read-only view with charts, tables, and formatted markdown
 * - Graceful handling of expired shares
 */
function SharedChatView() {
  const { token } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [chatData, setChatData] = useState(null);
  const [metadata, setMetadata] = useState(null);
  const [expired, setExpired] = useState(false);
  const [isSignedIn, setIsSignedIn] = useState(false);
  const [threadCollapsed, setThreadCollapsed] = useState({}); // Track collapse state per message
  const [deepReviewCollapsed, setDeepReviewCollapsed] = useState({}); // Track deep review collapse state (collapsed by default)
  const hasFetched = useRef(false); // Prevent double-fetch in React StrictMode

  // Scroll navigation state and refs
  const [showScrollTop, setShowScrollTop] = useState(false);
  const [showScrollBottom, setShowScrollBottom] = useState(false);
  const messagesEndRef = useRef(null);
  const lastScrollCheckRef = useRef(0);

  // Add robots meta tag to prevent search engine indexing
  useEffect(() => {
    const metaRobots = document.createElement('meta');
    metaRobots.name = 'robots';
    metaRobots.content = 'noindex, nofollow';
    document.head.appendChild(metaRobots);

    return () => {
      // Cleanup on unmount
      document.head.removeChild(metaRobots);
    };
  }, []);

  useEffect(() => {
    // Prevent duplicate API calls in React StrictMode (dev only)
    if (hasFetched.current) return;
    hasFetched.current = true;

    const fetchSharedChat = async () => {
      try {
        const headers = {};
        const userToken = localStorage.getItem('token');
        if (userToken) {
          headers.Authorization = `Bearer ${userToken}`;
          setIsSignedIn(true);
        }

        const response = await axios.get(
          `${API_ENDPOINTS.main}/chats/shared/${token}`,
          { headers }
        );

        if (response.data.expired) {
          setExpired(true);
          setMetadata(response.data.metadata);
        } else {
          setChatData(response.data.chat_data);
          setMetadata(response.data.metadata);
        }
      } catch (err) {
        console.error('Error fetching shared chat:', err);
        if (err.response?.status === 401) {
          setError({
            type: 'auth_required',
            message: err.response.data.detail || 'Sign in required to view this chat'
          });
        } else if (err.response?.status === 403) {
          setError({
            type: 'forbidden',
            message: err.response.data.detail || 'You do not have access to view this chat'
          });
        } else if (err.response?.status === 404) {
          setError({
            type: 'not_found',
            message: 'This shared chat does not exist or has been removed'
          });
        } else {
          setError({
            type: 'generic',
            message: 'Failed to load shared chat. Please try again.'
          });
        }
      } finally {
        setLoading(false);
      }
    };

    fetchSharedChat();
  }, [token]);

  // Initialize investigation thread collapse state based on mode
  useEffect(() => {
    if (chatData?.messages && metadata?.mode) {
      const initialState = {};
      chatData.messages.forEach((msg, index) => {
        if (msg.investigationThread) {
          // Virgo: collapsed by default (true)
          // Taurus: expanded by default (false)
          initialState[index] = metadata.mode === 'virgo';
        }
      });
      setThreadCollapsed(initialState);
    }
  }, [chatData, metadata]);

  // Initialize deep review collapse state - expanded if corrections found
  useEffect(() => {
    if (chatData?.messages) {
      const initialState = {};
      chatData.messages.forEach((msg, index) => {
        if (msg.deepReviewFindings) {
          // Check if investigation thread has the corrections marker
          const hasCorrections = msg.investigationThread?.includes('Deep Review found corrections');
          // Expanded (false) if corrections found, collapsed (true) otherwise
          initialState[index] = !hasCorrections;
        }
      });
      setDeepReviewCollapsed(initialState);
    }
  }, [chatData]);

  const toggleThreadCollapse = (messageIndex) => {
    setThreadCollapsed(prev => ({
      ...prev,
      [messageIndex]: !prev[messageIndex]
    }));
  };

  const toggleDeepReviewCollapse = (messageIndex) => {
    setDeepReviewCollapsed(prev => ({
      ...prev,
      [messageIndex]: prev[messageIndex] === undefined ? false : !prev[messageIndex] // Default is collapsed (undefined = collapsed), first click expands
    }));
  };

  // Scroll navigation functions (using window scroll for full-page layout)
  const scrollToTop = useCallback(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  // Window scroll detection for showing/hiding scroll buttons
  useEffect(() => {
    const handleScroll = () => {
      const now = Date.now();
      if (now - lastScrollCheckRef.current < 100) return; // Throttle to 100ms
      lastScrollCheckRef.current = now;

      const scrollTop = window.scrollY;
      const scrollHeight = document.documentElement.scrollHeight;
      const clientHeight = window.innerHeight;

      // Show arrows only if content is tall enough (>500px overflow)
      const hasEnoughContent = scrollHeight - clientHeight > 500;

      // Show top arrow if scrolled down more than 300px
      setShowScrollTop(hasEnoughContent && scrollTop > 300);

      // Show bottom arrow if not near bottom (>300px from bottom)
      setShowScrollBottom(hasEnoughContent && scrollHeight - scrollTop - clientHeight > 300);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const renderPlotImage = (plot, index) => {
    let plotSrc = '';

    if (typeof plot === 'string') {
      if (plot.startsWith('blob:') || plot.startsWith('data:') || plot.startsWith('http')) {
        plotSrc = plot;
      } else {
        plotSrc = `data:image/png;base64,${plot}`;
      }
    } else if (plot && typeof plot === 'object') {
      const plotData = plot.data || plot.base64 || plot.image || plot;
      if (typeof plotData === 'string') {
        if (plotData.startsWith('blob:') || plotData.startsWith('data:') || plotData.startsWith('http')) {
          plotSrc = plotData;
        } else {
          plotSrc = `data:image/png;base64,${plotData}`;
        }
      }
    }

    return plotSrc ? (
      <img
        src={plotSrc}
        alt={`Visualization ${index + 1}`}
        className="max-w-full h-auto rounded"
        onError={(e) => {
          console.error('Image failed to load');
          e.target.style.display = 'none';
        }}
      />
    ) : (
      <div className="text-red-400 text-sm">Visualization data not available</div>
    );
  };

  const renderTable = (table, index) => {
    if (!table || !table.headers || !table.rows) {
      return <div className="text-red-400 text-sm">Invalid table data</div>;
    }

    return (
      <div className="overflow-x-auto my-4">
        <table className="min-w-full divide-y divide-gray-300 dark:divide-gray-600 border border-gray-300 dark:border-gray-600">
          <thead className="bg-gray-100 dark:bg-gray-800">
            <tr>
              {table.headers.map((header, i) => (
                <th
                  key={i}
                  className="px-4 py-3 text-left text-xs font-semibold text-gray-900 dark:text-gray-100 uppercase tracking-wider border-r border-gray-300 dark:border-gray-600 last:border-r-0"
                >
                  {header}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200 dark:divide-gray-700 bg-white dark:bg-gray-900">
            {table.rows.map((row, rowIndex) => (
              <tr key={rowIndex} className="hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors">
                {row.map((cell, cellIndex) => (
                  <td
                    key={cellIndex}
                    className="px-4 py-3 text-sm text-gray-800 dark:text-gray-200 border-r border-gray-200 dark:border-gray-700 last:border-r-0"
                  >
                    {cell !== null && cell !== undefined ? String(cell) : ''}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-12 w-12 text-blue-600 dark:text-blue-400 animate-spin mx-auto mb-4" />
          <p className="text-gray-600 dark:text-gray-400">Loading shared chat...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center p-4">
        <div className="max-w-md w-full">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl p-6">
            <div className="flex items-center gap-3 mb-4">
              <AlertCircle className="h-8 w-8 text-red-600 dark:text-red-400" />
              <h3 className="text-xl font-semibold text-gray-900 dark:text-white">
                {error.type === 'auth_required' && '🔒 Sign In Required'}
                {error.type === 'forbidden' && '🚫 Access Denied'}
                {error.type === 'not_found' && '❌ Not Found'}
                {error.type === 'generic' && '❌ Error'}
              </h3>
            </div>
            <p className="text-gray-700 dark:text-gray-300 mb-4">{error.message}</p>

            {error.type === 'auth_required' && (
              <button
                onClick={() => navigate('/login')}
                className="w-full bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-4 rounded-lg transition-colors flex items-center justify-center gap-2"
              >
                <LogIn className="h-4 w-4" />
                Sign In to View
              </button>
            )}

            {error.type === 'forbidden' && (
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Contact the person who shared this link if you believe you should have access.
              </p>
            )}

            <button
              onClick={() => navigate('/')}
              className="w-full mt-3 bg-gray-200 hover:bg-gray-300 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-800 dark:text-gray-200 font-medium py-2 px-4 rounded-lg transition-colors"
            >
              Go to Home
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (expired) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center p-4">
        <div className="max-w-md w-full">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl p-6 text-center">
            <Clock className="h-16 w-16 text-amber-600 dark:text-amber-400 mx-auto mb-4" />
            <h3 className="text-2xl font-semibold text-gray-900 dark:text-white mb-2">
              Share Expired
            </h3>
            <p className="text-gray-700 dark:text-gray-300 mb-4">
              This shared chat expired on {new Date(metadata?.expires_at).toLocaleDateString()}.
            </p>
            <p className="text-gray-600 dark:text-gray-400 mb-6">
              Shared chats are available for 7 days. Sign up to create and share your own analyses!
            </p>
            <button
              onClick={() => navigate('/')}
              className="w-full bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-4 rounded-lg transition-colors"
            >
              Try VerbaGPT
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      {/* Top Banner */}
      <div className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 sticky top-0 z-10">
        <div className="max-w-5xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <span className="text-lg font-bold text-blue-600 dark:text-blue-400">VerbaGPT</span>
            <span className="text-gray-400 dark:text-gray-600">|</span>
            <span className="text-sm text-gray-600 dark:text-gray-400">Shared Chat</span>
          </div>
          <button
            onClick={() => navigate('/')}
            className="text-sm bg-blue-600 hover:bg-blue-700 text-white font-medium py-1.5 px-4 rounded-lg transition-colors"
          >
            Try VerbaGPT
          </button>
        </div>
      </div>

      {/* Chat Content */}
      <div className="max-w-5xl mx-auto px-4 py-8">
        {/* Metadata Header */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6 mb-6">
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
            {metadata?.title || 'Shared Analysis'}
          </h1>

          {/* Creator's Note - Prominent display */}
          {metadata?.note && (
            <div className="mb-4 p-4 bg-blue-50 dark:bg-blue-900/20 border-l-4 border-blue-500 rounded-r">
              <p className="text-sm text-gray-800 dark:text-gray-200 italic">
                "{metadata.note}"
              </p>
            </div>
          )}

          <div className="flex flex-wrap items-center gap-4 text-sm text-gray-600 dark:text-gray-400">
            <div className="flex items-center gap-2">
              <Database className="h-4 w-4" />
              <span>{metadata?.datasource || 'Unknown datasource'}</span>
            </div>
            <div className="flex items-center gap-2">
              <Eye className="h-4 w-4" />
              <span>{metadata?.view_count || 0} views</span>
            </div>
            <div className="flex items-center gap-2">
              <Clock className="h-4 w-4" />
              <span>Expires {new Date(metadata?.expires_at).toLocaleDateString()}</span>
            </div>
            {metadata?.mode && (
              <div className={`px-2 py-1 rounded text-xs font-medium uppercase ${
                metadata.mode === 'taurus'
                  ? 'bg-amber-100 dark:bg-amber-900/20 text-amber-700 dark:text-amber-300'
                  : 'bg-blue-100 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300'
              }`}>
                {metadata.mode}
              </div>
            )}
            {metadata?.visibility === 'organization' && (
              <div className="px-2 py-1 rounded bg-purple-100 dark:bg-purple-900/20 text-purple-700 dark:text-purple-300 text-xs font-medium">
                Organization Only
              </div>
            )}
          </div>

          {metadata?.attribution && (
            <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
              <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
                <UserIcon className="h-4 w-4" />
                <span>Shared by {metadata.attribution.email}</span>
              </div>
            </div>
          )}
        </div>

        {/* Messages */}
        <div className="space-y-4">
          {chatData?.messages?.map((message, index) => (
            <div
              key={index}
              className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}
            >
              <div className={`max-w-4xl ${message.role === 'user' ? 'ml-12' : 'mr-12'}`}>
                {message.role === 'user' ? (
                  <div className="bg-blue-600 text-white p-4 rounded-lg whitespace-pre-wrap">
                    {message.content}
                  </div>
                ) : (
                  <div className="space-y-4">
                    {/* Investigation Thread (Virgo: collapsed, Taurus: expanded) */}
                    {message.investigationThread && (
                      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
                        <button
                          onClick={() => toggleThreadCollapse(index)}
                          className="w-full px-4 py-3 flex items-center justify-between bg-gray-50 dark:bg-gray-700 hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors"
                        >
                          <span className="text-sm font-medium text-gray-700 dark:text-gray-300 flex items-center gap-2">
                            {threadCollapsed[index] ? (
                              <ChevronRight className="h-4 w-4" />
                            ) : (
                              <ChevronDown className="h-4 w-4" />
                            )}
                            Investigation Thread
                            <span className="text-xs text-gray-500 dark:text-gray-400">
                              ({metadata?.mode === 'taurus' ? 'Agent steps' : 'Execution details'})
                            </span>
                          </span>
                        </button>
                        {!threadCollapsed[index] && (
                          <div className="p-4 bg-gray-50 dark:bg-gray-900 border-t border-gray-200 dark:border-gray-700 overflow-x-auto">
                            {metadata?.mode === 'taurus' ? (
                              // Taurus: collapsible PDFs + markdown-lite rendering
                              <TaurusContentRenderer content={message.investigationThread} />
                            ) : (
                              // Virgo: plain pre-formatted text
                              <pre className="text-xs text-gray-800 dark:text-gray-200 whitespace-pre-wrap font-mono">
                                {message.investigationThread}
                              </pre>
                            )}
                          </div>
                        )}
                      </div>
                    )}

                    {/* Charts */}
                    {message.charts && message.charts.length > 0 && (
                      <div className="space-y-4">
                        {message.charts.map((chart, chartIndex) => (
                          <div
                            key={chartIndex}
                            className="bg-gray-100 dark:bg-gray-800 rounded-lg overflow-hidden border border-gray-300 dark:border-gray-600"
                          >
                            <div className="px-4 py-2 bg-gray-200 dark:bg-gray-700 border-b border-gray-300 dark:border-gray-600">
                              <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                                Visualization {chartIndex + 1}
                                {chart.title && ` - ${chart.title}`}
                              </span>
                            </div>
                            <div className="p-4 bg-white dark:bg-gray-900">
                              {renderPlotImage(chart, chartIndex)}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Content - Final Answer/Review (Only show if not empty - Taurus has empty content by default) */}
                    {message.content && message.content.trim() && (
                      <div className={`p-5 rounded-lg border shadow-sm ${
                        message.isReview
                          ? metadata?.mode === 'taurus'
                            ? 'bg-gradient-to-br from-amber-50 to-orange-50 dark:from-amber-900/20 dark:to-orange-900/20 border-amber-200 dark:border-amber-700'
                            : 'bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 border-blue-200 dark:border-blue-700'
                          : 'bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700'
                      }`}>
                        {message.isReview && (
                          <div className={`flex items-center gap-2 mb-3 pb-2 ${
                            metadata?.mode === 'taurus'
                              ? 'border-b border-amber-200 dark:border-amber-700'
                              : 'border-b border-blue-200 dark:border-blue-700'
                          }`}>
                            <svg
                              className={`h-5 w-5 ${
                                metadata?.mode === 'taurus'
                                  ? 'text-amber-600 dark:text-amber-400'
                                  : 'text-blue-600 dark:text-blue-400'
                              }`}
                              fill="none"
                              viewBox="0 0 24 24"
                              stroke="currentColor"
                            >
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                            <span className={`text-sm font-semibold ${
                              metadata?.mode === 'taurus'
                                ? 'text-amber-900 dark:text-amber-200'
                                : 'text-blue-900 dark:text-blue-200'
                            }`}>
                              {metadata?.mode === 'taurus' ? 'Taurus Summary' : 'Virgo Review'}
                            </span>
                          </div>
                        )}
                        <div className="prose dark:prose-invert max-w-none">
                          <ReactMarkdown
                            remarkPlugins={[remarkGfm]}
                            components={{
                              p: ({ children }) => <p className="text-gray-800 dark:text-gray-100 mb-3 leading-relaxed">{children}</p>,
                              h1: ({ children }) => <h1 className="text-gray-900 dark:text-white text-2xl font-bold mb-4 mt-6">{children}</h1>,
                              h2: ({ children }) => <h2 className="text-gray-900 dark:text-white text-xl font-bold mb-3 mt-5">{children}</h2>,
                              h3: ({ children }) => <h3 className="text-gray-900 dark:text-white text-lg font-semibold mb-2 mt-4">{children}</h3>,
                              strong: ({ children }) => <strong className="text-gray-900 dark:text-white font-semibold">{children}</strong>,
                              ul: ({ children }) => <ul className="text-gray-800 dark:text-gray-100 list-disc list-inside mb-3 space-y-1">{children}</ul>,
                              ol: ({ children }) => <ol className="text-gray-800 dark:text-gray-100 list-decimal list-inside mb-3 space-y-1">{children}</ol>,
                              li: ({ children }) => <li className="text-gray-800 dark:text-gray-100 mb-1">{children}</li>,
                              table: ({ children }) => (
                                <div className="overflow-x-auto my-4">
                                  <table className="min-w-full divide-y divide-gray-300 dark:divide-gray-600 border border-gray-300 dark:border-gray-600">
                                    {children}
                                  </table>
                                </div>
                              ),
                              thead: ({ children }) => <thead className="bg-gray-100 dark:bg-gray-800">{children}</thead>,
                              tbody: ({ children }) => <tbody className="divide-y divide-gray-200 dark:divide-gray-700 bg-white dark:bg-gray-900">{children}</tbody>,
                              tr: ({ children }) => <tr className="hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors">{children}</tr>,
                              th: ({ children }) => <th className="px-4 py-3 text-left text-xs font-semibold text-gray-900 dark:text-gray-100 uppercase tracking-wider border-r border-gray-300 dark:border-gray-600 last:border-r-0">{children}</th>,
                              td: ({ children }) => <td className="px-4 py-3 text-sm text-gray-800 dark:text-gray-200 border-r border-gray-200 dark:border-gray-700 last:border-r-0">{children}</td>,
                              code: ({ node, inline, children, ...props }) => {
                                return inline ? (
                                  <code className="bg-gray-100 dark:bg-gray-900 text-gray-900 dark:text-gray-100 px-1.5 py-0.5 rounded text-sm font-mono" {...props}>
                                    {children}
                                  </code>
                                ) : (
                                  <pre className="bg-gray-100 dark:bg-gray-900 text-gray-900 dark:text-gray-100 p-4 rounded-lg overflow-x-auto">
                                    <code className="text-sm font-mono" {...props}>
                                      {children}
                                    </code>
                                  </pre>
                                );
                              }
                            }}
                          >
                            {message.content}
                          </ReactMarkdown>
                        </div>
                      </div>
                    )}

                    {/* Tables */}
                    {message.tables && message.tables.length > 0 && (
                      <div className="space-y-4">
                        {message.tables.map((table, tableIndex) => (
                          <div
                            key={tableIndex}
                            className="bg-white dark:bg-gray-800 rounded-lg overflow-hidden border border-gray-200 dark:border-gray-700"
                          >
                            <div className="px-4 py-2 bg-gray-100 dark:bg-gray-700 border-b border-gray-200 dark:border-gray-600">
                              <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                                Table {tableIndex + 1}
                              </span>
                            </div>
                            <div className="p-4">
                              {renderTable(table, tableIndex)}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Deep Review Findings - Collapsible (collapsed by default for shared view, Virgo and Taurus) */}
                    {message.deepReviewFindings && (
                      <div className="mt-4 bg-gradient-to-br from-purple-50 to-indigo-50 dark:from-purple-900/20 dark:to-indigo-900/20 rounded-lg border border-purple-200 dark:border-purple-700 overflow-hidden">
                        <button
                          onClick={() => toggleDeepReviewCollapse(index)}
                          className="w-full px-4 py-3 flex items-center justify-between hover:bg-purple-100/50 dark:hover:bg-purple-900/30 transition-colors"
                        >
                          <div className="flex items-center gap-2">
                            <ShieldCheck className="h-5 w-5 text-purple-600 dark:text-purple-400" />
                            <span className="text-sm font-semibold text-purple-900 dark:text-purple-200">
                              Deep Review Findings
                            </span>
                          </div>
                          {deepReviewCollapsed[index] === false ? (
                            <ChevronUp className="h-4 w-4 text-purple-600 dark:text-purple-400" />
                          ) : (
                            <ChevronDown className="h-4 w-4 text-purple-600 dark:text-purple-400" />
                          )}
                        </button>
                        {deepReviewCollapsed[index] === false && (
                          <div className="px-4 pb-4 pt-2 border-t border-purple-200 dark:border-purple-700">
                            <div className="prose dark:prose-invert max-w-none">
                              <ReactMarkdown
                                remarkPlugins={[remarkGfm]}
                                components={{
                                  p: ({ children }) => <p className="text-gray-800 dark:text-gray-100 mb-2 text-sm leading-relaxed">{children}</p>,
                                  h3: ({ children }) => <h3 className="text-gray-900 dark:text-white text-base font-semibold mb-2 mt-3">{children}</h3>,
                                  strong: ({ children }) => <strong className="text-gray-900 dark:text-white font-semibold">{children}</strong>,
                                  ul: ({ children }) => <ul className="text-gray-800 dark:text-gray-100 list-disc list-inside mb-2 space-y-1 text-sm">{children}</ul>,
                                  li: ({ children }) => <li className="text-gray-800 dark:text-gray-100">{children}</li>,
                                }}
                              >
                                {message.deepReviewFindings}
                              </ReactMarkdown>
                            </div>
                          </div>
                        )}
                      </div>
                    )}

                    {/* Response time display */}
                    {message.responseTime && (
                      <div className="flex justify-start mt-2">
                        <span className="text-xs text-gray-400 dark:text-gray-500">
                          {message.responseTime < 60000
                            ? `${(message.responseTime / 1000).toFixed(1)}s`
                            : `${Math.floor(message.responseTime / 60000)}m ${Math.round((message.responseTime % 60000) / 1000)}s`}
                        </span>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          ))}
          {/* Scroll anchor for scrollToBottom */}
          <div ref={messagesEndRef} />
        </div>

        {/* CTA Banner - Context-aware based on auth status */}
        <div className="mt-12">
          {!isSignedIn ? (
            /* Not Signed In - Acquisition */
            <div className="bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 border-2 border-blue-200 dark:border-blue-700 rounded-xl p-8 text-center">
              <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2">
                Create Your Own AI-Powered Analysis
              </h3>
              <p className="text-gray-600 dark:text-gray-400 mb-6">
                Upload your data, ask questions in plain English, get instant visualizations.
              </p>
              <div className="flex flex-col sm:flex-row gap-3 justify-center items-center">
                <button
                  onClick={() => navigate('/')}
                  className="bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 px-8 rounded-lg transition-colors shadow-lg hover:shadow-xl"
                >
                  Get Started Free →
                </button>
                <button
                  onClick={() => navigate('/')}
                  className="bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300 font-medium py-3 px-8 rounded-lg border border-gray-300 dark:border-gray-600 transition-colors"
                >
                  Sign In
                </button>
              </div>
              <p className="text-sm text-gray-500 dark:text-gray-500 mt-4">
                No credit card required • Free tier available
              </p>
            </div>
          ) : (
            /* Signed In - Engagement */
            <div className="bg-purple-50 dark:bg-purple-900/20 border-2 border-purple-200 dark:border-purple-700 rounded-xl p-6 text-center">
              <p className="text-gray-700 dark:text-gray-300 mb-4">
                👋 <strong>Shared chats are view-only.</strong> Want to create your own analysis?
              </p>
              <button
                onClick={() => navigate('/')}
                className="bg-purple-600 hover:bg-purple-700 text-white font-semibold py-3 px-8 rounded-lg transition-colors inline-flex items-center justify-center gap-2"
              >
                <MessageSquare className="h-5 w-5" />
                Open VerbaGPT
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Scroll Navigation Buttons - Fixed position */}
      {(showScrollTop || showScrollBottom) && (
        <div className="fixed right-6 bottom-6 flex flex-col gap-2 z-20">
          {showScrollTop && (
            <button
              onClick={scrollToTop}
              className="p-2 rounded-full bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600 shadow-lg opacity-70 hover:opacity-100 transition-opacity"
              title="Scroll to top"
            >
              <ChevronUp className="w-5 h-5 text-gray-600 dark:text-gray-300" />
            </button>
          )}
          {showScrollBottom && (
            <button
              onClick={scrollToBottom}
              className="p-2 rounded-full bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600 shadow-lg opacity-70 hover:opacity-100 transition-opacity"
              title="Scroll to bottom"
            >
              <ChevronDown className="w-5 h-5 text-gray-600 dark:text-gray-300" />
            </button>
          )}
        </div>
      )}
    </div>
  );
}

export default SharedChatView;
